/**
 * Inspection Series request serializer.
 *
 * Converts a normalized ListingQueryState<SeriesFilters> to the URLSearchParams
 * format expected by the existing /inspection_series/listing.json Kendo endpoint.
 *
 * Contract requirements:
 *  - Search is included when nonblank (IP-15819; Inspections parity).
 *  - Multi-select arrays are order-normalized (sorted) before inclusion.
 *  - Scope (building_id / account_id) is appended as top-level query params.
 *  - Page/sort parameters use the same Kendo format as the Inspections serializer.
 *
 * Endpoint parameters (from inspection_series_datatable.rb):
 *  - building_id / account_id: scope identifiers (read via params[:building_id], params[:account_id])
 *  - filter[filters][n][field], [operator], [value]: individual filter entries
 *  - filter[filters][n][value][rangeType/startDate/endDate/fieldType]: date range sub-params
 *  - sort[0][field], sort[0][dir]: sort specification
 *  - page, pageSize, skip, take: pagination
 *
 * The endpoint filter fields and their Ruby handlers:
 *  - account:        filter_by_account   → accounts.id eq [array]
 *  - building:       filter_by_building  → building_id eq [array]
 *  - territory:      filter_by_territory → buildings.territory_id eq [array|sentinel]
 *  - expirationDate: filter_by_expiration_date → end_date date range
 *  - status:         filter_by_status    → cancelled eq bool
 *  - autoExtend:     filter_by_auto_extend → has_auto_extend eq bool
 *  - expiredSeries:  include_expired     → filters out expired when false/nil
 */
import { normalizeMultiSelect } from '@hooks/listings';
import { EXPIRED_SERIES_ALL_VALUE } from '../types';
// ---------------------------------------------------------------------------
// Kendo date range adapter
// ---------------------------------------------------------------------------
const KENDO_FIELD_TYPE = 'dateRange';
/** Fallback rangeType for calendar-entered dates when no rangeType is stored. */
const KENDO_CUSTOM_RANGE = 'custom_range';
/** Converts SeriesDateRange → Kendo date-range sub-parameters.
 *  Emits the stored rangeType (e.g. "next_month") so the backend can apply the
 *  correct date filter. Defaults to 'custom_range' when rangeType is absent. */
function appendDateRange(params, prefix, range) {
    var _a, _b, _c;
    const rangeType = (_a = range.rangeType) !== null && _a !== void 0 ? _a : KENDO_CUSTOM_RANGE;
    params.append(`${prefix}[rangeType]`, rangeType);
    params.append(`${prefix}[startDate]`, (_b = range.start) !== null && _b !== void 0 ? _b : '');
    params.append(`${prefix}[endDate]`, (_c = range.end) !== null && _c !== void 0 ? _c : '');
    params.append(`${prefix}[fieldType]`, KENDO_FIELD_TYPE);
}
// ---------------------------------------------------------------------------
// Page / sort helpers
// ---------------------------------------------------------------------------
function appendPageParams(params, pageNumber, pageSize) {
    const skip = (pageNumber - 1) * pageSize;
    const take = pageSize;
    params.append('page', String(pageNumber));
    params.append('pageSize', String(take));
    params.append('skip', String(skip));
    params.append('take', String(take));
}
function appendSortParams(params, sort) {
    if (!sort)
        return;
    params.append('sort[0][field]', sort.field);
    params.append('sort[0][dir]', sort.direction);
}
// ---------------------------------------------------------------------------
// Scope helper
// ---------------------------------------------------------------------------
/**
 * Appends the scope identifier as a top-level query param.
 * The Ruby datatable reads params[:building_id] and params[:account_id] directly
 * to determine the base query (parent_building / parent_account).
 */
function appendScopeParams(params, state) {
    if (state.scope.type === 'building') {
        params.append('building_id', String(state.scope.id));
    }
    else if (state.scope.type === 'account') {
        params.append('account_id', String(state.scope.id));
    }
}
function buildFilterEntries(filters) {
    const entries = [];
    const addArray = (field, values) => {
        const normalized = normalizeMultiSelect([...values]);
        if (normalized.length === 0)
            return;
        entries.push({ field, operator: 'eq', value: normalized });
    };
    const addBoolean = (field, value) => {
        if (value === null)
            return;
        entries.push({ field, operator: 'eq', value: String(value) });
    };
    // Multi-selects
    addArray('account', filters.account);
    addArray('building', filters.building);
    addArray('territory', filters.territory);
    // Boolean selects
    addBoolean('status', filters.status);
    addBoolean('autoExtend', filters.autoExtend);
    // expiredSeries: false = non-expired, true = expired only, null = All.
    if (filters.expiredSeries === null) {
        entries.push({ field: 'expiredSeries', operator: 'eq', value: EXPIRED_SERIES_ALL_VALUE });
    }
    else {
        entries.push({ field: 'expiredSeries', operator: 'eq', value: String(filters.expiredSeries) });
    }
    return entries;
}
function appendFilterParams(params, filters) {
    const entries = buildFilterEntries(filters);
    // Date range (handled separately because its value is nested sub-params).
    let dateIdx = entries.length;
    const hasExpirationDate = filters.expirationDate.start || filters.expirationDate.end;
    if (entries.length === 0 && !hasExpirationDate)
        return;
    params.append('filter[logic]', 'and');
    entries.forEach((entry, i) => {
        const prefix = `filter[filters][${i}]`;
        params.append(`${prefix}[field]`, entry.field);
        params.append(`${prefix}[operator]`, entry.operator);
        if (Array.isArray(entry.value)) {
            entry.value.forEach((v) => {
                params.append(`${prefix}[value][]`, String(v));
            });
        }
        else {
            params.append(`${prefix}[value]`, String(entry.value));
        }
    });
    if (hasExpirationDate) {
        const prefix = `filter[filters][${dateIdx}]`;
        appendDateRange(params, `${prefix}[value]`, filters.expirationDate);
        params.append(`${prefix}[field]`, 'expirationDate');
        params.append(`${prefix}[operator]`, 'eq');
        dateIdx += 1;
    }
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Full request serializer: scope + page + sort + filters + search.
 * Used by the React Query fetch function.
 */
export function serializeSeriesRequest(state) {
    const params = new URLSearchParams();
    appendScopeParams(params, state);
    appendPageParams(params, state.page.number, state.page.size);
    appendSortParams(params, state.sort);
    appendFilterParams(params, state.filters);
    if (state.search)
        params.append('search', state.search);
    return params;
}
